/**
 * Payment destinations — where players are told to send money.
 *
 * The API guards this with `payment_destination.manage`, which only superadmin
 * holds, and the route's own comment explains why it is stricter than deposit
 * approval: approving a deposit credits one player a known amount against
 * evidence a human checked, while changing a destination silently redirects
 * *every future deposit* to an account of the editor's choosing, and the
 * players sending money have no way to notice.
 *
 * This screen inherits that gravity. Both writes confirm, and the account
 * number is shown in full rather than truncated — a digit changed in the middle
 * of an account number is exactly the edit that would otherwise pass a glance.
 * Rows can be edited in place (the same write that created them), and
 * decommissioning is a Deactivate, never a delete.
 */

import { Fragment, useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { api, type CreateDestinationInput, type UpdateDestinationInput } from '../api/client.ts';
import { can } from '../auth.ts';
import { etb, parseAmount } from '../format.ts';
import { ConfirmButton, ErrorBanner, Field, Spinner } from '../components/ui.tsx';
import type { AdminPaymentDestinationView, AdminView, BankMethod } from '../api/types.ts';

interface FormState {
  method: BankMethod;
  label: string;
  account: string;
  accountName: string;
  instructions: string;
  minAmount: string;
  maxAmount: string;
  sortOrder: string;
}

function blankForm(): FormState {
  return {
    method: 'telebirr',
    label: '',
    account: '',
    accountName: '',
    instructions: '',
    minAmount: '',
    maxAmount: '',
    sortOrder: '',
  };
}

function formFrom(d: AdminPaymentDestinationView): FormState {
  return {
    method: d.method,
    label: d.label,
    account: d.account,
    accountName: d.accountName ?? '',
    instructions: d.instructions ?? '',
    minAmount: d.minAmount ?? '',
    maxAmount: d.maxAmount ?? '',
    sortOrder: String(d.sortOrder),
  };
}

type Parsed =
  | { ok: true; payload: CreateDestinationInput }
  | { ok: false; reason: string };

/** The shared create/edit field set, and the santim-parsing shared by both. */
function parseForm(form: FormState): Parsed {
  const min = form.minAmount.trim() !== '' ? parseAmount(form.minAmount) : null;
  const max = form.maxAmount.trim() !== '' ? parseAmount(form.maxAmount) : null;
  if ((min && !min.ok) || (max && !max.ok)) {
    return { ok: false, reason: 'Amounts must look like 50 or 50.00' };
  }
  return {
    ok: true,
    payload: {
      method: form.method,
      label: form.label.trim(),
      account: form.account.trim(),
      ...(form.accountName.trim() ? { accountName: form.accountName.trim() } : {}),
      ...(form.instructions.trim() ? { instructions: form.instructions.trim() } : {}),
      ...(min?.ok ? { minAmount: min.santim } : {}),
      ...(max?.ok ? { maxAmount: max.santim } : {}),
      ...(form.sortOrder.trim() !== '' ? { sortOrder: Number(form.sortOrder) } : {}),
    },
  };
}

function Fields({
  form,
  setForm,
  disabled,
}: {
  form: FormState;
  setForm: (patch: Partial<FormState>) => void;
  disabled?: boolean;
}) {
  const set = (key: keyof FormState) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ [key]: e.target.value } as Partial<FormState>);

  return (
    <>
      <Field label="Method">
        <select className="input" value={form.method} disabled={disabled} onChange={set('method')}>
          <option value="telebirr">telebirr</option>
          <option value="cbe">cbe</option>
        </select>
      </Field>
      <Field label="Label" hint="What players see in the picker">
        <input className="input" value={form.label} disabled={disabled} onChange={set('label')} />
      </Field>
      <Field label="Account number">
        <input className="input" value={form.account} disabled={disabled} onChange={set('account')} />
      </Field>
      <Field
        label="Account name"
        hint="Shown to players so they can check the transfer is arriving where they expect."
      >
        <input
          className="input"
          value={form.accountName}
          disabled={disabled}
          onChange={set('accountName')}
        />
      </Field>
      <Field label="Instructions" hint="Optional — extra text players see next to the account.">
        <input
          className="input"
          value={form.instructions}
          disabled={disabled}
          onChange={set('instructions')}
        />
      </Field>
      <div className="pair">
        <Field label="Minimum (optional)">
          <input
            className="input"
            inputMode="decimal"
            value={form.minAmount}
            disabled={disabled}
            onChange={set('minAmount')}
          />
        </Field>
        <Field label="Maximum (optional)">
          <input
            className="input"
            inputMode="decimal"
            value={form.maxAmount}
            disabled={disabled}
            onChange={set('maxAmount')}
          />
        </Field>
      </div>
      <Field label="Sort order" hint="Lower numbers appear first in the player picker.">
        <input
          className="input"
          inputMode="numeric"
          value={form.sortOrder}
          disabled={disabled}
          onChange={set('sortOrder')}
        />
      </Field>
    </>
  );
}

export function Destinations({ me }: { me: AdminView }) {
  const [destinations, setDestinations] = useState<AdminPaymentDestinationView[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const [create, setCreate] = useState<FormState>(blankForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [edit, setEdit] = useState<FormState>(blankForm);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { destinations: next } = await api.destinations();
      setDestinations(next);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const manage = can(me.role, 'payment_destination.manage');

  async function createDestination(): Promise<void> {
    const parsed = parseForm(create);
    if (!parsed.ok) {
      setError(new Error(parsed.reason));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createDestination(parsed.payload);
      setCreate(blankForm());
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  function beginEdit(d: AdminPaymentDestinationView): void {
    setEdit(formFrom(d));
    setEditingId(d.id);
    setError(null);
  }

  async function saveEdit(): Promise<void> {
    if (editingId === null) return;
    const parsed = parseForm(edit);
    if (!parsed.ok) {
      setError(new Error(parsed.reason));
      return;
    }
    // The create shape is the superset; send every field so an empty one
    // clears what was there (PATCH nulls clear, per the route's schema).
    const payload: UpdateDestinationInput = {
      method: parsed.payload.method,
      label: parsed.payload.label,
      account: parsed.payload.account,
      accountName: parsed.payload.accountName ?? null,
      instructions: parsed.payload.instructions ?? null,
      minAmount: parsed.payload.minAmount ?? null,
      maxAmount: parsed.payload.maxAmount ?? null,
      sortOrder: parsed.payload.sortOrder ?? 0,
    };
    setBusy(true);
    setError(null);
    try {
      await api.updateDestination(editingId, payload);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function setActive(id: string, active: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.setDestinationActive(id, active);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <header className="pagehead">
        <h1>Payment destinations</h1>
      </header>

      <ErrorBanner error={error} />

      {!manage ? (
        <p className="muted">
          You can see these but not change them — only a superadmin can.
        </p>
      ) : null}

      {destinations === null && !error ? <Spinner /> : null}

      {destinations ? (
        <table className="table" role="table">
          <thead role="rowgroup">
            <tr role="row">
              <th scope="col" role="columnheader">Label</th>
              <th scope="col" role="columnheader">Method</th>
              <th scope="col" role="columnheader">Account</th>
              <th scope="col" role="columnheader">Name</th>
              <th scope="col" role="columnheader">Limits</th>
              <th scope="col" role="columnheader">Live</th>
              <th scope="col" role="columnheader" />
            </tr>
          </thead>
          <tbody role="rowgroup">
            {destinations.map((d) => (
              <Fragment key={d.id}>
                <tr role="row" className={d.active ? '' : 'row--off'}>
                  <td role="cell" data-label="Label">
                    {d.label}
                    {d.instructions ? (
                      <div className="muted small">{d.instructions}</div>
                    ) : null}
                  </td>
                  <td role="cell" data-label="Method">{d.method}</td>
                  <td role="cell" data-label="Account">
                    {/* Full, never truncated. See the note at the top. */}
                    <code>{d.account}</code>
                  </td>
                  <td role="cell" data-label="Name">{d.accountName ?? <span className="muted">—</span>}</td>
                  <td role="cell" data-label="Limits" className="muted">
                    {d.minAmount ? `min ${etb(d.minAmount)}` : ''}
                    {d.minAmount && d.maxAmount ? ' · ' : ''}
                    {d.maxAmount ? `max ${etb(d.maxAmount)}` : ''}
                  </td>
                  <td role="cell" data-label="Live">{d.active ? 'yes' : 'no'}</td>
                  <td role="cell" data-label="">
                    {manage ? (
                      <div className="actions actions--inline">
                        <button
                          type="button"
                          className="button"
                          disabled={busy}
                          onClick={() => beginEdit(d)}
                        >
                          Edit
                        </button>
                        <ConfirmButton
                          className={`button ${d.active ? 'button--danger' : ''}`}
                          confirm={
                            d.active
                              ? 'Stop showing this account to players?'
                              : 'Start sending players to this account?'
                          }
                          disabled={busy}
                          onConfirm={() => void setActive(d.id, !d.active)}
                        >
                          {d.active ? 'Deactivate' : 'Activate'}
                        </ConfirmButton>
                      </div>
                    ) : null}
                  </td>
                </tr>
                {editingId === d.id ? (
                  <tr role="row" className="editrow">
                    <td role="cell" data-label="" colSpan={7}>
                      <div className="card card--compact">
                        <h3>Editing {d.label}</h3>
                        <div className="fieldgrid">
                          <Fields
                            form={edit}
                            setForm={(patch) => setEdit((prev) => ({ ...prev, ...patch }))}
                            disabled={busy}
                          />
                        </div>
                        <div className="actions">
                          <ConfirmButton
                            className="button button--primary"
                            confirm="Save this edit? It changes where players are told to send money."
                            disabled={busy || !edit.label.trim() || !edit.account.trim()}
                            onConfirm={() => void saveEdit()}
                          >
                            {busy ? 'Saving…' : 'Save changes'}
                          </ConfirmButton>
                          <button
                            type="button"
                            className="button button--ghost"
                            disabled={busy}
                            onClick={() => setEditingId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      ) : null}

      {manage ? (
        <div className="card">
          <h2>Add a destination</h2>
          <div className="fieldgrid">
            <Fields
              form={create}
              setForm={(patch) => setCreate((prev) => ({ ...prev, ...patch }))}
              disabled={busy}
            />
          </div>
          <ConfirmButton
            className="button button--primary"
            confirm="Add this account? Players will be told to send money to it."
            disabled={busy || !create.label.trim() || !create.account.trim()}
            onConfirm={() => void createDestination()}
          >
            Add destination
          </ConfirmButton>
        </div>
      ) : null}
    </section>
  );
}